import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from "@/components/ui/button";
import { GraduationCap, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScholarshipSelector from "@/components/scholarship/ScholarshipSelector";
import ScholarshipResults from "@/components/scholarship/ScholarshipResults";
import { supabase } from "@/integrations/supabase/client";
import PageHead from "@/components/PageHead";

const programs = [
  { value: "computer-science", label: "Computer Science" },
  { value: "business-administration", label: "Business Administration" },
  { value: "engineering", label: "Engineering" },
  { value: "medicine", label: "Medicine" },
  { value: "arts-humanities", label: "Arts and Humanities" },
  { value: "social-sciences", label: "Social Sciences" },
  { value: "law", label: "Law" },
  { value: "architecture", label: "Architecture" }
];

const countries = [
  { value: "united-states", label: "United States" },
  { value: "united-kingdom", label: "United Kingdom" },
  { value: "canada", label: "Canada" },
  { value: "australia", label: "Australia" },
  { value: "germany", label: "Germany" },
  { value: "france", label: "France" },
  { value: "netherlands", label: "Netherlands" },
  { value: "sweden", label: "Sweden" }
];

interface University {
  name: string;
  country: string;
  programs: string[];
  hasScholarship: boolean;
  annualTuition: number;
  scholarshipDetails?: string;
  websiteUrl?: string;
}

// Updated blog posts array with most recent first
const blogPosts = [
  {
    id: "common-mistakes",
    title: "Common Mistakes International Applicants Make When Seeking Sponsorship",
    description: "Learn about the common mistakes international applicants make when seeking visa sponsorship and how to avoid them.",
    image: "/lovable-uploads/38300a84-9f3d-405b-b1a1-9ae41d0e6f15.png",
    date: "May 2, 2025",
    readTime: "12 min read"
  },
  {
    id: "employer-sponsorship",
    title: "What Employers Look for When Sponsoring a Work Visa",
    description: "Understand the key factors that influence employers' decisions to sponsor foreign workers for visas.",
    image: "/lovable-uploads/280595a6-6bf9-4a18-a381-50f89cbd24ba.png",
    date: "April 30, 2025",
    readTime: "14 min read"
  },
  {
    id: "non-tech-visa-jobs",
    title: "Visa-Sponsored Jobs for Non-Tech Professionals in 2025",
    description: "Explore diverse opportunities for non-technical professionals seeking international employment with visa sponsorship.",
    image: "/lovable-uploads/9c9d1274-143d-4912-9cb6-964cc86d2cc7.png",
    date: "April 29, 2025",
    readTime: "13 min read"
  },
  {
    id: "top-countries-visa-jobs",
    title: "Top 10 Countries Offering Visa-Sponsored Jobs in 2025",
    description: "Discover the best destinations for international professionals seeking work opportunities with visa sponsorship.",
    image: "/lovable-uploads/043878bf-2912-4bc0-a66e-654e594d4237.png",
    date: "April 27, 2025",
    readTime: "15 min read"
  }
];

const ScholarshipsPage = () => {
  const { toast } = useToast();
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [scholarshipsOnly, setScholarshipsOnly] = useState(false);
  const [maxTuitionFee, setMaxTuitionFee] = useState(150000);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [universities, setUniversities] = useState<University[]>([]);
  
  const searchScholarships = async () => {
    setIsLoading(true);
    setHasSearched(true);
    
    try {
      // Get program and country labels
      const programLabel = programs.find(p => p.value === selectedProgram)?.label || '';
      const countryLabel = countries.find(c => c.value === selectedCountry)?.label || '';
      
      console.log("Searching for scholarships with:", {
        program: programLabel,
        country: countryLabel,
        scholarshipsOnly,
        maxTuition: maxTuitionFee
      });
      
      // Call the Supabase edge function to search scholarships using OpenAI
      const { data, error } = await supabase.functions.invoke('search-scholarships', {
        body: {
          program: programLabel,
          country: countryLabel,
          scholarshipsOnly,
          maxTuition: maxTuitionFee
        }
      });
      
      if (error) {
        console.error("Supabase functions error:", error);
        throw new Error(error.message || 'Failed to search scholarships');
      }
      
      console.log('Search results:', data);
      
      if (data && Array.isArray(data.data)) {
        setUniversities(data.data);
        if (data.data.length === 0) {
          toast({
            title: "Search Results",
            description: "No matching universities found.",
            variant: "default",
          });
        } else {
          toast({
            title: "Search Complete",
            description: `Found ${data.data.length} universities matching your criteria.`,
            variant: "default",
          });
        }
      } else {
        // Handle case where the data structure is not as expected
        setUniversities([]);
        toast({
          title: "Search Results",
          description: "No matching universities found.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error searching scholarships:', error);
      toast({
        title: "Search Error",
        description: "Failed to search for scholarships. Please try again.",
        variant: "destructive",
      });
      setUniversities([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSearch = () => {
    searchScholarships();
  };

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead title="Scholarships" description="Discover and compare international scholarship opportunities across top universities worldwide." />
      <Navbar />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
              <GraduationCap className="h-8 w-8" />
              Find University Programs & Scholarships
            </h1>
            <p className="text-xl text-gray-600 mt-4 mx-auto max-w-2xl">
              Discover and compare international scholarship opportunities across top universities worldwide.
            </p>
          </div>
          
          <ScholarshipSelector
            selectedProgram={selectedProgram}
            selectedCountry={selectedCountry}
            scholarshipsOnly={scholarshipsOnly}
            maxTuitionFee={maxTuitionFee}
            onProgramChange={setSelectedProgram}
            onCountryChange={setSelectedCountry}
            onScholarshipsOnlyChange={setScholarshipsOnly}
            onMaxTuitionFeeChange={(value) => setMaxTuitionFee(value[0])}
            programs={programs}
            countries={countries}
          />

          <div className="flex justify-center mb-8">
            <Button 
              onClick={handleSearch}
              className="bg-primary hover:bg-primary/90"
              disabled={isLoading}
            >
              {isLoading ? (
                <>Loading...</>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Search Programs
                </>
              )}
            </Button>
          </div>

          {hasSearched && (
            <ScholarshipResults universities={universities} isLoading={isLoading} />
          )}

          <div className="mt-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Get smarter, with our blog.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.id}`} key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm block transition-transform duration-300 hover:-translate-y-1">
                  <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                  <div className="p-6">
                    <div className="text-xs text-purple-600 font-semibold mb-2">{post.date}</div>
                    <h3 className="font-bold text-lg mb-2">{post.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">{post.description}</p>
                    <div className="text-sm text-gray-500">{post.readTime}</div>
                    <div className="mt-3 text-purple-600">Read article →</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/blog">
                <button className="border border-gray-300 px-6 py-2 rounded-md text-gray-800 font-medium">
                  See all articles
                </button>
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ScholarshipsPage;
